import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["batchIdInput", "batchDisplay", "noBatchMessage"]

  connect() {
    // Auto-focus the batch ID input when page loads
    this.batchIdInputTarget.focus()
  }

  scan(event) {
    event.preventDefault()

    const batchId = this.batchIdInputTarget.value.trim()

    if (!batchId) {
      return
    }

    // Clear the input for next scan
    const inputValue = batchId
    this.batchIdInputTarget.value = ''

    // Make AJAX request to scan endpoint
    fetch('/batches/scan', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ batch_id: inputValue })
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        this.showError(data.error)
      } else {
        this.displayBatch(data.batch)
      }
      // Re-focus input for next scan
      this.batchIdInputTarget.focus()
    })
    .catch(error => {
      console.error('Error:', error)
      this.showError('An error occurred while scanning')
      this.batchIdInputTarget.focus()
    })
  }

  displayBatch(batch) {
    this.noBatchMessageTarget.classList.add('hidden')
    this.batchDisplayTarget.classList.remove('hidden')

    const html = `
      <div class="border-t border-gray-200 pt-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-xl font-semibold text-gray-900">Batch: ${batch.batch_id}</h3>
            <p class="text-sm text-gray-500 mt-1">${batch.timestamp}</p>
          </div>
          <div class="text-right">
            <p class="text-sm text-gray-500">Work Orders: ${batch.workorder_count}</p>
            <p class="text-sm text-gray-500">Total Items: ${batch.item_count}</p>
          </div>
        </div>

        <div class="flex justify-end mb-4">
          <a href="/batches/${batch.id}/reprint"
             data-turbo="false"
             class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
            </svg>
            Print Batch Paperwork
          </a>
        </div>

        <div class="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 class="text-sm font-medium text-gray-900 mb-3">Work Orders</h4>
          <div class="space-y-3">
            ${this.renderWorkorders(batch.workorders)}
          </div>
        </div>

        <div class="flex justify-end">
          <a href="/batches/${batch.id}/reprint"
             data-turbo="false"
             class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
            </svg>
            Print Batch Paperwork
          </a>
        </div>
      </div>
    `

    this.batchDisplayTarget.innerHTML = html
  }

  renderWorkorders(workorders) {
    return workorders.map(workorder => `
      <div class="bg-white rounded border border-gray-200 p-3">
        <div class="flex items-center justify-between mb-2">
          <p class="font-medium text-gray-900">Order #${workorder.order_number}</p>
          <span class="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
            ${workorder.item_numbers.length} items
          </span>
        </div>
        <div class="text-xs text-gray-600 space-y-1">
          ${workorder.item_numbers.map(item => `
            <div>
              <span>Item #${item.item_number}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `).join('')
  }

  showError(message) {
    this.batchDisplayTarget.classList.add('hidden')
    this.noBatchMessageTarget.classList.remove('hidden')
    this.noBatchMessageTarget.innerHTML = `
      <div class="rounded-md bg-red-50 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error</h3>
            <div class="mt-2 text-sm text-red-700">
              <p>${message}</p>
            </div>
          </div>
        </div>
      </div>
    `
  }
}
